import { useState, useEffect } from "react";

// @mui material components
import Card from "@mui/material/Card";
import TextField from "@mui/material/TextField";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDAlert from "components/MDAlert";

// Material Dashboard 2 React example components
import DashboardLayout from "core/LayoutContainers/DashboardLayout";

import AuthService from "../../services/auth-service";
import userService from "../../services/user-service";

const UserProfile = () => {
  const [notification, setNotification] = useState(false);
  const [user, setUser] = useState({
    firstName: "",
    lastName: "",
    email: "",
  });

  const [errors, setErrors] = useState({
    firstNameError: false,
    lastNameError: false,
    emailError: false,
  });

  const getUserData = async () => {
    const userEmail = localStorage.getItem("userEmail");
    const response = await AuthService.getProfile(userEmail);
    setUser({
      firstName: response.firstName || "",
      lastName: response.lastName || "",
      email: response.email || "",
    });
  };

  useEffect(() => {
    getUserData();
  }, []);

  useEffect(() => {
    if (notification) {
      setTimeout(() => setNotification(false), 5000);
    }
  }, [notification]);

  const changeHandler = (e) => {
    setUser({ ...user, [e.target.name]: e.target.value });
  };

  const submitHandler = async (e) => {
    e.preventDefault();
    
    // Validation for empty strings
    if (user.firstName.trim().length === 0) {
      setErrors({ ...errors, firstNameError: true });
      return;
    }
    
    if (user.lastName.trim().length === 0) {
      setErrors({ ...errors, lastNameError: true });
      return;
    }

    // Get userId from localStorage
    const userId = localStorage.getItem("userId");
    
    if (!userId) {
      return;
    }

    // Prepare data for update (only firstName and lastName as per requirement)
    const updateData = {
      firstName: user.firstName.trim(),
      lastName: user.lastName.trim()
    };

    try {
      await userService.updateUser(userId, updateData);
      setErrors({ firstNameError: false, lastNameError: false, emailError: false });
      setNotification(true);
    } catch (error) {
      console.error("Error updating profile:", error);
      // You might want to show an error notification here
    }
  };

  // Get initials for avatar
  const getInitials = () => {
    const first = user.firstName ? user.firstName.charAt(0).toUpperCase() : "";
    const last = user.lastName ? user.lastName.charAt(0).toUpperCase() : "";
    return first + last || "U";
  };

  const inputStyles = {
    "& .MuiOutlinedInput-root": {
      borderRadius: 3,
      backgroundColor: "#f8f9fa",
      "& fieldset": { borderColor: "#e0e0e0" },
      "&:hover fieldset": { borderColor: "#7c88a2" },
      "&.Mui-focused fieldset": { borderColor: "#7c88a2", borderWidth: 2 },
    },
  };

  return (
    <DashboardLayout>
      <MDBox display="flex" justifyContent="center" py={6}>
        <Card sx={{ maxWidth: 500, width: "100%", p: 4, borderRadius: 3 }}>
          {/* Avatar Section */}
          <MDBox display="flex" flexDirection="column" alignItems="center" mb={4}>
            <MDBox
              display="flex"
              alignItems="center"
              justifyContent="center"
              width={100}
              height={100}
              borderRadius="50%"
              sx={{
                background: "linear-gradient(135deg, #7c88a2 0%, #4f5c7a 100%)",
                boxShadow: "0 4px 20px rgba(124, 136, 162, 0.3)"
              }}
            >
              <MDTypography variant="h3" color="white" fontWeight="medium">
                {getInitials()}
              </MDTypography>
            </MDBox>
          </MDBox>

          {/* Notification */}
          {notification && (
            <MDAlert color="info" sx={{ mb: 3 }}>
              <MDTypography variant="body2" color="white">
                Your profile has been updated
              </MDTypography>
            </MDAlert>
          )}

          {/* Form */}
          <MDBox component="form" onSubmit={submitHandler}>
            {/* First Name */}
            <MDBox mb={3}>
              <MDTypography variant="button" fontWeight="medium" color="text" display="block" mb={1}>
                First Name
              </MDTypography>
              <TextField
                fullWidth
                name="firstName"
                value={user.firstName}
                onChange={(e) => {
                  changeHandler(e);
                  if (e.target.value.trim().length > 0) {
                    setErrors({ ...errors, firstNameError: false });
                  }
                }}
                error={errors.firstNameError}
                helperText={errors.firstNameError ? "First name cannot be empty" : ""}
                sx={inputStyles}
              />
            </MDBox>

            {/* Last Name */}
            <MDBox mb={3}>
              <MDTypography variant="button" fontWeight="medium" color="text" display="block" mb={1}>
                Last Name
              </MDTypography>
              <TextField
                fullWidth
                name="lastName"
                value={user.lastName}
                onChange={(e) => {
                  changeHandler(e);
                  if (e.target.value.trim().length > 0) {
                    setErrors({ ...errors, lastNameError: false });
                  }
                }}
                error={errors.lastNameError}
                helperText={errors.lastNameError ? "Last name cannot be empty" : ""}
                sx={inputStyles}
              />
            </MDBox>

            {/* Email - Disabled */}
            <MDBox mb={4}>
              <MDTypography variant="button" fontWeight="medium" color="text" display="block" mb={1}>
                Email Address
              </MDTypography>
              <TextField
                fullWidth
                type="email"
                name="email"
                value={user.email}
                disabled
                sx={{
                  ...inputStyles,
                  "& .MuiOutlinedInput-root": {
                    ...inputStyles["& .MuiOutlinedInput-root"],
                    backgroundColor: "#f0f0f0", // Light gray background for disabled field
                  }
                }}
              />
            </MDBox>

            {/* Save Button */}
            <MDButton
              variant="gradient"
              color="info"
              fullWidth
              size="large"
              type="submit"
              sx={{
                borderRadius: 3,
                py: 1.5,
                textTransform: "none",
                fontSize: "1rem",
              }}
            >
              Save Changes
            </MDButton>
          </MDBox>
        </Card>
      </MDBox>
    </DashboardLayout>
  );
};

export default UserProfile;